/**
 * Modo de cor da aplicação, compartilhado por contexto.
 *
 * A Kumo lê o atributo `data-mode` do documento, e os gráficos precisam saber o modo
 * para escolher paleta e cor de eixo. Um provedor só mantém os dois em acordo — modo
 * trocado num lugar e esquecido no outro produz eixo escuro sobre fundo escuro.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  THEME_STORAGE_KEY,
  resolveThemeMode,
  toggleThemeMode,
  type ThemeMode,
} from "../lib/theme-mode.js";

/** O que o contexto oferece: o modo, o atalho booleano e a troca. */
interface ThemeModeContextValue {
  readonly mode: ThemeMode;
  readonly isDark: boolean;
  readonly toggle: () => void;
}

/** Contexto do modo de cor; nulo fora do provedor, para o uso indevido falhar alto. */
const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

/**
 * Lê a escolha guardada.
 *
 * Navegador em modo privado pode recusar o armazenamento local com exceção; aí vale o
 * padrão, em vez de a tela inteira não abrir.
 */
function readStoredMode(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Entradas do provedor. */
interface ThemeModeProviderProps {
  readonly children: ReactNode;
}

/** Provedor do modo de cor: aplica o atributo no documento e guarda a escolha. */
export function ThemeModeProvider({ children }: ThemeModeProviderProps): React.ReactElement {
  const [mode, setMode] = useState<ThemeMode>(() => resolveThemeMode(readStoredMode()));

  useEffect(() => {
    const root = document.documentElement;
    root.dataset["mode"] = mode;
    // `light-dark()` dos tokens da Kumo resolve pelo `color-scheme` efetivo; sem ele,
    // o atributo muda e as cores ficam no modo anterior.
    root.style.colorScheme = mode;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // Armazenamento indisponível: a escolha vale só enquanto a aba estiver aberta.
    }
  }, [mode]);

  const toggle = useCallback(() => setMode((current) => toggleThemeMode(current)), []);
  const value = useMemo(() => ({ mode, isDark: mode === "dark", toggle }), [mode, toggle]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

/** Modo de cor corrente. Falha alto fora do provedor, em vez de devolver um padrão mudo. */
export function useThemeMode(): ThemeModeContextValue {
  const value = useContext(ThemeModeContext);
  if (value === null) {
    throw new Error("useThemeMode usado fora de ThemeModeProvider");
  }
  return value;
}
